import { RowSelectionState } from '@tanstack/react-table';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { lotsApi } from '../../lib/apis/lots';

import { LotsValidationHeader } from '../../components/lots-validation/lots-validation-header';
import { LotsColumnPreset } from '../../components/lots-validation/lots-column-preset';
import { useLotsExport } from '../../components/lots-validation/use-lots-export';
import { useLotsFilterOptions } from '../../components/lots-validation/use-lots-filters';
import { useLotsStats } from '../../components/lots-validation/use-lots-stats';
import LotsValidationTable from '../../components/lots-validation-table/lots-validation-table';

import { useContractsQueries } from '../../lib/queries/contracts';

import { ErrorPage } from './page.error';

import { Permission, usePermissions } from '@auth/data-access';
import { useFiltersStore } from '@data-access';
import { FullMainHeight } from '@ui';

export function LotsValidationPage() {
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [columnPreset, setColumnPreset] = useState<LotsColumnPreset>(
    LotsColumnPreset.Tonnages,
  );
  const { can } = usePermissions();
  const canManage = can(Permission.ManageContracts);
  const contractsQueries = useContractsQueries();
  const filters = useFiltersStore.use['lots']().filters;
  const sorting = useFiltersStore.use['lots']().sorting;
  const page = useFiltersStore.use['lots']().pagination.pageIndex + 1;

  const lotsQuery = useQuery({
    ...contractsQueries.lotsList({ page, sorting, filters }),
    placeholderData: keepPreviousData,
  });

  const statsQuery = useLotsStats(filters);

  const { years, species } = useLotsFilterOptions(lotsQuery.data?.results ?? []);

  const selectedIds = useMemo(
    () =>
      Object.keys(rowSelection)
        .filter((id) => id !== 'all')
        .map((id) => Number(id)),
    [rowSelection],
  );

  const refetchLots = () => {
    void lotsQuery.refetch();
    void statsQuery.refetch();
  };

  const validateSelected = useMutation({
    mutationFn: async (ids: number[]) => {
      await Promise.all(ids.map((id) => lotsApi.validate(id)));
    },
    onSuccess: () => {
      refetchLots();
      setRowSelection({});
    },
  });

  const handleValidateSelected = () => {
    const pendingIds = selectedIds.filter((id) => {
      const lot = lotsQuery.data?.results.find((row) => row.id === id);
      return lot && !lot.is_validated;
    });
    if (pendingIds.length > 0) {
      validateSelected.mutate(pendingIds);
    }
  };

  const { handleExport, isExportLoading } = useLotsExport({
    filters,
    rowSelection,
    selectedIds,
  });

  return (
    <FullMainHeight.Root>
      <FullMainHeight.Header>
        <LotsValidationHeader
          columnPreset={columnPreset}
          isValidateSelectedLoading={validateSelected.isPending}
          onColumnPresetChange={setColumnPreset}
          isExportLoading={isExportLoading}
          onExport={handleExport}
          onValidateSelected={canManage ? handleValidateSelected : undefined}
          pending={statsQuery.data?.pending}
          selectedCount={selectedIds.length}
          species={species}
          statsLoading={statsQuery.isLoading}
          total={statsQuery.data?.total}
          validated={statsQuery.data?.validated}
          years={years}
        />
      </FullMainHeight.Header>
      <FullMainHeight.Content>
        <LotsValidationTable
          columnPreset={columnPreset}
          data={lotsQuery.data?.results ?? []}
          isFetching={lotsQuery.isFetching}
          isLoading={lotsQuery.isLoading}
          isPlaceholderData={lotsQuery.isPlaceholderData}
          onValidateSuccess={refetchLots}
          rowCount={lotsQuery.data?.count ?? 0}
          rowSelection={rowSelection}
          setRowSelection={setRowSelection}
        />
      </FullMainHeight.Content>
    </FullMainHeight.Root>
  );
}

LotsValidationPage.Error = ErrorPage;

export default LotsValidationPage;
